import { useState, useMemo, useCallback } from 'react';
import { I18n } from '@aws-amplify/core';
import { message } from 'antd';
import { useUpdateNotificationStatus, useGetNotificationStatus } from '@/hooks';
import { useApplicationContext } from '@/context/v1/Application/context';
import { DATA_EXTRACTION_NOTIFICATION_METHOD, DATA_EXTRACTION_NOTIFICATION_TYPE } from '@/utils/constants';

const getNotificationTexts = (isCurrentlyEnabled: boolean) => {
	return {
		title: isCurrentlyEnabled
			? 'Do you want to disable email notifications?'
			: 'Would you like to receive email notifications when the extractions are available?',
		toastMessage: isCurrentlyEnabled
			? 'Notifications disabled successfully!'
			: 'Notifications enabled successfully!'
	};
};

export const useNotificationButton = () => {
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const { organization } = useApplicationContext();
	const { mutateAsync: updateNotificationStatus, isLoading: isUpdating } = useUpdateNotificationStatus();

	const {
		data: notificationPreferences,
		isLoading,
		refetch
	} = useGetNotificationStatus({
		organization_id: organization.id
	});

	const isEnabled = useMemo(() => {
		if (!notificationPreferences) return false;

		return notificationPreferences.some(
			(pref) =>
				pref.notification_type === DATA_EXTRACTION_NOTIFICATION_TYPE.DATA_EXTRACTION_COMPLETED &&
				pref.notification_method === DATA_EXTRACTION_NOTIFICATION_METHOD.EMAIL
		);
	}, [notificationPreferences]);

	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);

	const handleCancelModal = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const handleConfirmModal = useCallback(async () => {
		await updateNotificationStatus({
			organization_id: organization.id,
			notification_preferences: [
				{
					type: DATA_EXTRACTION_NOTIFICATION_TYPE.DATA_EXTRACTION_COMPLETED,
					method: DATA_EXTRACTION_NOTIFICATION_METHOD.EMAIL,
					enabled: !isEnabled
				}
			]
		});
		message.success(I18n.get(getNotificationTexts(isEnabled).toastMessage));
		await refetch();
		setIsModalOpen(false);
	}, [isEnabled, organization.id, refetch, updateNotificationStatus]);

	const modalTitle = I18n.get(getNotificationTexts(isEnabled).title);

	return {
		isLoading,
		isUpdating,
		isEnabled,
		isModalOpen,
		modalTitle,
		handleOpenModal,
		handleCancelModal,
		handleConfirmModal
	};
};
